// Upload + drag-and-drop + clipboard-paste handling for the chat composer.
// Owns its own `isUploading` and `isDragging` state; everything else flows
// through `chatStore`. Exported as a hook so ChatComposer can wire the
// returned handlers directly to its DOM events.
import { useCallback, useRef, useState, type ClipboardEvent, type DragEvent } from 'react';
import { chatStore } from '@/lib/chatStore';
import { MAX_ATTACHMENTS_PER_MESSAGE } from '@onestone/shared/chatAttachments';
import { uploadFiles, validateFiles } from './chatUploadUtils';

interface UseChatFileUpload {
  isDragging: boolean;
  isUploading: boolean;
  handleDragEnter: (e: DragEvent) => void;
  handleDragLeave: (e: DragEvent) => void;
  handleDragOver: (e: DragEvent) => void;
  handleDrop: (e: DragEvent) => void;
  handleFilePickerChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handlePaste: (e: ClipboardEvent<HTMLTextAreaElement>) => void;
}

export function useChatFileUpload(): UseChatFileUpload {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const dragCounterRef = useRef(0);

  const handleFiles = useCallback(
    async (files: File[]) => {
      if (isUploading || chatStore.getState().isStreaming) return;
      const total = chatStore.getState().pendingAttachments.length + files.length;
      if (total > MAX_ATTACHMENTS_PER_MESSAGE) {
        chatStore.setUploadError(`Maximum ${MAX_ATTACHMENTS_PER_MESSAGE} files per message.`);
        return;
      }
      const { valid, errors } = validateFiles(files);
      if (errors.length > 0) chatStore.setUploadError(errors.join(' '));
      if (valid.length === 0) return;
      setIsUploading(true);
      try {
        const result = await uploadFiles(valid);
        const unmatched = [...valid];
        for (const record of result.files) {
          // The picked file this record is for (the server may refuse some), shown
          // from memory — no request, so it works on every client.
          const i = unmatched.findIndex((f) => f.name === record.name && f.size === record.size);
          const [picked] = i >= 0 ? unmatched.splice(i, 1) : [];
          chatStore.addPendingAttachment(picked ? { ...record, localUrl: URL.createObjectURL(picked) } : record);
        }
        if (result.errors.length > 0) chatStore.setUploadError(result.errors.join(' '));
      } catch (e) {
        chatStore.setUploadError(e instanceof Error ? e.message : 'Upload failed');
      } finally {
        setIsUploading(false);
      }
    },
    [isUploading],
  );

  const handleDragEnter = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (dragCounterRef.current === 1) setIsDragging(true);
  }, []);
  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) setIsDragging(false);
  }, []);
  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);
  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current = 0;
      setIsDragging(false);
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) void handleFiles(files);
    },
    [handleFiles],
  );

  const handleFilePickerChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      if (files.length > 0) void handleFiles(files);
      e.target.value = '';
    },
    [handleFiles],
  );

  const handlePaste = useCallback(
    (e: ClipboardEvent<HTMLTextAreaElement>) => {
      const files = Array.from(e.clipboardData.items)
        .filter((item) => item.kind === 'file')
        .map((item) => item.getAsFile())
        .filter((f): f is File => f !== null);
      if (files.length > 0) {
        e.preventDefault();
        void handleFiles(files);
      }
    },
    [handleFiles],
  );

  return {
    isDragging,
    isUploading,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleFilePickerChange,
    handlePaste,
  };
}
